import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import Loader from '__components/Kizen/Loader';
import BaseEmbedHtml from '__components/Kizen/EmbedHtml';
import { getReportSeverity } from './importProgress';
import type { ImportProgressState } from './usePluginKZNImporter';

const EmbedHtml = BaseEmbedHtml as ComponentType<{
  sandbox?: string;
  children?: string;
}>;

type ImportProgressStepProps = {
  importProgress: NonNullable<ImportProgressState>;
  onContinue?: () => void;
  continueDisabled?: boolean;
  showEmail?: boolean;
};

export const ImportProgressStep = ({
  importProgress,
  onContinue,
  continueDisabled,
  showEmail = false,
}: ImportProgressStepProps) => {
  const { t } = useTranslation();

  const polling = importProgress.phase === 'polling';
  const outcome = polling ? null : importProgress.outcome;
  const report = outcome && outcome.kind !== 'email' ? outcome.report : null;
  const severity = getReportSeverity(report);

  return (
    <>
      {polling ? (
        <div className="flex items-center gap-[10px]">
          <Loader loading size={14} paddingTop={0} />
          <Typography>
            {t('Importing the app schema. This may take a minute.')}
          </Typography>
        </div>
      ) : null}
      {showEmail && report?.email_body ? (
        // The result email already states the outcome and lists what was imported. Same
        // sandboxed embed the Export/Import settings page uses to show this report.
        <EmbedHtml sandbox="allow-popups allow-popups-to-escape-sandbox">
          {report.email_body}
        </EmbedHtml>
      ) : (
        <>
          {outcome?.kind === 'success' ? (
            <>
              <Typography>
                {severity === 'errors'
                  ? t(
                      'The import completed with errors. Check your email for details.'
                    )
                  : t('The app schema was imported successfully.')}
              </Typography>
              {severity === 'warnings' ? (
                <>
                  <Spacer size={10} />
                  <Typography>
                    {t(
                      'The import completed with warnings. Check your email for details.'
                    )}
                  </Typography>
                </>
              ) : null}
            </>
          ) : null}
          {outcome?.kind === 'failed' ? (
            <Typography>
              {t(
                'The app schema import failed. Check your email for further details.'
              )}
            </Typography>
          ) : null}
        </>
      )}
      {outcome?.kind === 'email' ? (
        <Typography>
          {t(
            "The result of the import couldn't be confirmed. You will receive an email with the result."
          )}
        </Typography>
      ) : null}
      {onContinue ? (
        <>
          <Spacer size={20} />
          <div className="flex">
            <Button
              onClick={onContinue}
              disabled={!outcome || continueDisabled}
              qa={{ id: 'plugin-schema-import-continue' }}
            >
              {t('Continue')}
            </Button>
          </div>
        </>
      ) : null}
    </>
  );
};
